/**
 * @module app.shared
 */

import { HttpMethod, IApiBatch, IApiBatchOptions, IApiRequest, IRestApi } from 'app/shared/lib/api/api-types';

interface IBatchStep {
    method: HttpMethod;
    action: IApiRequest;
    description: string;
    result: any;
    error: any;
}

interface IBatchInternals {
    steps: IBatchStep[];
}

/**
 * Represents multiple API actions to execute in sequence
 */
export class ApiBatch implements IApiBatch {

    private readonly internals: IBatchInternals = {
        steps: []
    };

    /**
     * Queue a POST action to execute as part of the batch
     * @param action
     * @param description optional description of step
     * @returns `this`
     */
    public post(action: IApiRequest, description: string = ''): this {
        return this.add('post', action, description);
    }

    /**
     * Queue a PUT action to execute as part of the batch
     * @param action
     * @param description optional description of step
     * @returns `this`
     */
    public put(action: IApiRequest, description: string = ''): this {
        return this.add('put', action, description);
    }

    /**
     * Queue a DELETE action to execute as part of the batch
     * @param action
     * @param description optional description of step
     * @returns `this`
     */
    public delete(action: IApiRequest, description: string = ''): this {
        return this.add('delete', action, description);
    }

    /**
     * Execute the batch of actions
     * @param api the API to execute against
     * @param options configuration for the batch
     * @returns a Promise with an array of results from all actions in the batch
     */
    public async execute(api: IRestApi, options: IApiBatchOptions): Promise<any[]> {
        const progress = options.progress;

        let stepNumber: number = 0;
        for(const step of this.internals.steps) {
            try {
                step.result = await api[step.method](step.action, options);
            }
            catch(stepError) {
                step.result = undefined;
                step.error = stepError;
            }

            if(progress) {
                progress.step(++stepNumber, this.internals.steps.length, step.description);
            }
        }
        if(progress) {
            progress.dismiss();
        }
        return this.internals.steps.map(s => s.result);
    }

    private add(method: HttpMethod, action: IApiRequest, description: string = ''): this {
        this.internals.steps.push({
            method,
            action,
            description,
            result: undefined,
            error: undefined
        });
        return this;
    }

}
